/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import * as XLSX from 'xlsx'

import { IMPORT_FIELDS, MAX_IMPORT_ROWS, type ImportField, type ImportRowInput } from './types'

/** Header spellings accepted for each column, compared lowercased with spaces, dashes and underscores removed. */
const HEADER_ALIASES: Record<ImportField, string[]> = {
  email: ['email', 'emailaddress'],
  firstName: ['first', 'firstname'],
  lastName: ['last', 'lastname', 'surname'],
  company: ['company', 'companyname'],
  username: ['username', 'user', 'login'],
  dashboard: ['dashboard', 'dashboardfrom', 'copydashboardfrom'],
}

const REQUIRED_HEADERS: ImportField[] = ['email', 'firstName', 'lastName', 'company']

export const TEMPLATE_HEADERS = ['email', 'first', 'last', 'company', 'username', 'dashboard']

const headerKey = (header: string) => header.toLowerCase().replace(/[\s_-]+/g, '')

const fieldForHeader = (header: string): ImportField | undefined => {
  const key = headerKey(header)
  return IMPORT_FIELDS.find((field) => HEADER_ALIASES[field].includes(key))
}

/** The file's bytes. FileReader rather than File.arrayBuffer() — same result, and jsdom has only the former. */
export const readFileBytes = (file: Blob): Promise<ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read'))
    reader.readAsArrayBuffer(file)
  })

export type ParseResult = { ok: true; rows: ImportRowInput[] } | { ok: false; error: string }

/**
 * Read a CSV or spreadsheet into import rows. The first sheet's first row is the
 * header; columns may be in any order. Blank rows are dropped. Everything is
 * kept as text — the server judges the values.
 */
export function parseImportFile(data: ArrayBuffer): ParseResult {
  let sheet: XLSX.WorkSheet | undefined
  try {
    // raw: true keeps CSV cells as typed — no turning "0123" into 123 or a name into a date.
    const workbook = XLSX.read(data, { type: 'array', raw: true })
    sheet = workbook.Sheets[workbook.SheetNames[0]]
  } catch {
    return { ok: false, error: 'The file could not be read. Upload a CSV or Excel file.' }
  }
  if (!sheet) return { ok: false, error: 'The file is empty.' }

  const table = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '', raw: false, blankrows: false })
  if (table.length === 0) return { ok: false, error: 'The file is empty.' }

  const headers = table[0].map((cell) => String(cell).trim())
  const fields = headers.map(fieldForHeader)
  const unknown = headers.filter((header, index) => header && !fields[index])
  if (unknown.length > 0) {
    return {
      ok: false,
      error: `Unknown column${unknown.length > 1 ? 's' : ''}: ${unknown.join(', ')}. Expected: ${TEMPLATE_HEADERS.join(', ')}.`,
    }
  }
  const repeated = IMPORT_FIELDS.filter((field) => fields.filter((f) => f === field).length > 1)
  if (repeated.length > 0) return { ok: false, error: `Column repeated: ${repeated.join(', ')}.` }
  const missing = REQUIRED_HEADERS.filter((field) => !fields.includes(field))
  if (missing.length > 0) {
    const names = missing.map((field) => HEADER_ALIASES[field][0])
    return { ok: false, error: `Missing column${missing.length > 1 ? 's' : ''}: ${names.join(', ')}.` }
  }

  const rows: ImportRowInput[] = []
  for (const cells of table.slice(1)) {
    const row: ImportRowInput = { email: '', firstName: '', lastName: '', company: '', username: '', dashboard: '' }
    fields.forEach((field, index) => {
      if (field) row[field] = String(cells[index] ?? '').trim()
    })
    if (IMPORT_FIELDS.some((field) => row[field])) rows.push(row)
  }

  if (rows.length === 0) return { ok: false, error: 'The file has a header but no rows.' }
  if (rows.length > MAX_IMPORT_ROWS) {
    return {
      ok: false,
      error: `The file has ${rows.length} rows; at most ${MAX_IMPORT_ROWS} can be imported at once. Split it into smaller files.`,
    }
  }
  return { ok: true, rows }
}
